import type {ReactElement} from 'react';
import {useState} from 'react';
import React from 'react';
import styles from './ai-prompt-box-attachment-preview.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize} from '@Components/icon-v2/icon.types';
import type {AiPromptBoxAttachmentPreviewProps} from '@Components/ai-prompt-box/components/ai-prompt-box-attachment-preview/ai-prompt-box-attachment-preview.types';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {isVideoUrl} from '@Utils/media.util';

export function AiPromptBoxAttachmentPreview({previewUrl, index, onRemove, onPreviewClick, size}: AiPromptBoxAttachmentPreviewProps): ReactElement {
  const [loaded, setLoaded] = useState(false);

  const sizeDimensionMap = {
    [AiPromptBoxSize.SMALL]: 48,
    [AiPromptBoxSize.MEDIUM]: 56,
    [AiPromptBoxSize.SINGLE_LINE]: 48,
    [AiPromptBoxSize.SMALL_EXTENDED]: 48,
  };

  const sizeClassMap = {
    [AiPromptBoxSize.SMALL]: styles.small,
    [AiPromptBoxSize.MEDIUM]: styles.medium,
    [AiPromptBoxSize.SINGLE_LINE]: styles.small,
    [AiPromptBoxSize.SMALL_EXTENDED]: styles.small,
  };

  const dimension = sizeDimensionMap[size];
  const isVideo = isVideoUrl(previewUrl);

  const handleMediaLoad = (): void => {
    setLoaded(true);
  };

  const handleMediaClick = (): void => {
    onPreviewClick(index);
  };

  const renderMedia = (): ReactElement => {
    const commonClassName = `${styles.attachmentItem} ${loaded ? '' : '_hidden'}`;

    if (isVideo) {
      return <video src={previewUrl} className={commonClassName} onLoadedData={handleMediaLoad} onClick={handleMediaClick} muted />;
    }

    return <img src={previewUrl} className={commonClassName} onLoad={handleMediaLoad} onClick={handleMediaClick} alt="" />;
  };

  return (
    <div className={`${styles.attachment} ${sizeClassMap[size]}`}>
      <SkeletonBrick height={dimension} width={dimension} className={`${styles.attachmentItem} ${loaded ? '_hidden' : ''} spacing-m-b-2`} />
      {renderMedia()}
      <Icon
        className={styles.removeAttachmentButton}
        icon={'icon-close'}
        size={IconSize.SIZE_ICON_12}
        shape={IconShape.CIRCLE}
        onClick={() => {
          onRemove(index);
        }}
      />
    </div>
  );
}

export default React.memo(AiPromptBoxAttachmentPreview, (prevProps, nextProps) => {
  return prevProps.previewUrl === nextProps.previewUrl && prevProps.size === nextProps.size && prevProps.index === nextProps.index;
});
